import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import HeadingWrapper from "@components/common/HeadingWrapper";
import {
  DSBadge,
  DSCardWrapper,
  DSLinkButton,
} from "@components/design-system/molecules";
import { OpenPipButton } from "@components/pip/OpenPipButton";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
  faCloudDownload,
  faInfoCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tooltip,
} from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import ClientSummary from "./ClientSummary";
import ClientSummaryFreePlanState from "./ClientSummary/ClientSummaryFreePlanState";
import CustomForm from "./CustomForm";
import DentalChart from "./DentalChart";
import { KeyPatientInfoFormFields } from "./KeyPatientInfoFormFields";
import { NoteHeader } from "./NoteHeader";
import SOAPForm from "./SOAPForm";
import { usePremiumFeature } from "../../.././../hooks";
import SEND_EXPORT_NOTE_EMAIL from "../../../../graphql/mutations/SendExportNoteEmail";
import Twemoji from "../../../../Twemoji";
import { AttachmentsPreview } from "../../../attachments/AttachmentsPreview";
import { alert } from "../../../common/Alert";
import AttachmentSelection from "../../../common/inputs/AttachmentSelection";
import ExportModal from "../../../email/ExportModal";
import PromptFeedbackWidget from "../../common/PromptFeedbackWidget";
import { ExportNoteInline } from "../../ExportNoteInline";
import LegacySOAPForm from "../../legacy/LegacySOAPForm";

const DELETE_ATTACHMENT = gql`
  mutation deleteAttachment($attachmentUuid: String) {
    deleteAttachment(attachmentUuid: $attachmentUuid) {
      ok
    }
  }
`;

export function NoteForm({
  note,
  refetchNote,
  isQuicknote,
  isSuperSOAP,
  isTemplate = false,
  isScribeEdit,
  getValues,
  patientUUID,
  control,
  onSaveAsDraft,
  onSubmitNote,
}) {
  const [exportExpanded, setExportExpanded] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { useLegacyNotes } = useFlags();
  const shouldUseCustomForm = useWatch({
    control,
    name: "shouldUseCustomForm",
  });
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();

  const isDentalChart = note?.jobType === "dental_record";

  const shouldShowFreePlanState = !hasFeatureAccess(
    UserPermissions.CLIENT_SUMMARY_ENABLED,
  );

  const shouldShowFeedbackWidget = hasFeatureAccess(
    UserPermissions.NOTE_FEEDBACK_ENABLED,
  );

  const emailMethods = useForm();
  const { control: emailControl, handleSubmit: emailHandleSubmit } =
    emailMethods;

  const [sendExportNoteEmail] = useMutation(SEND_EXPORT_NOTE_EMAIL);
  const [deleteAttachment] = useMutation(DELETE_ATTACHMENT);

  const handleDeleteAttachment = async (attachmentUuid) => {
    try {
      await deleteAttachment({
        variables: { attachmentUuid },
      });
      alert("success", "Attachment deleted.");
      refetchNote?.();
    } catch {
      alert(
        "error",
        "Failed to delete attachment. Please try again.",
      );
    }
  };

  const onEmailSend = async (data) => {
    try {
      await sendExportNoteEmail({
        variables: {
          toEmail: data?.toEmail,
          grantedNoteUuid: note?.uuid,
          isForAssistant: false,
          specialInstructions: data?.specialInstructions,
          areAttachmentsIncluded: data?.includeAttachments,
          shouldEmbedEmailExports:
            data?.exportType === "embeddedText",
        },
      });
      alert("success", `Email sent to ${data?.toEmail}!`);
      setShowEmailModal(false);
    } catch (error) {
      console.error("Error sending export email:", error);
      alert("error", "An error occurred. Please try again later.");
    }
  };

  return (
    <div className="mb-5" data-testid="note-form-container">
      <ExportModal
        shouldShowModal={showEmailModal}
        hideModal={() => setShowEmailModal(false)}
        handleSubmit={emailHandleSubmit}
        onSend={onEmailSend}
        control={emailControl}
        methods={emailMethods}
      />

      <NoteHeader
        note={note}
        refetchNote={refetchNote}
        control={control}
        isTemplate={isTemplate}
        isQuicknote={isQuicknote}
        isSuperSOAP={isSuperSOAP}
        isScribeEdit={isScribeEdit}
        patientUUID={patientUUID}
        onSaveAsDraft={onSaveAsDraft}
        onSubmitNote={onSubmitNote}
      />

      <div className="mb-5" data-testid="note-form-container-inner">
        {shouldUseCustomForm ? (
          <div className="flex flex-col md:flex-row md:items-center md:space-x-10 space-y-10 md:space-y-0">
            <CustomForm control={control} note={note} />
            {isDentalChart && (
              <div className="w-[300px] md:w-[400px]">
                <DentalChart
                  record={note?.customFormFields[0].textValue}
                />
              </div>
            )}
          </div>
        ) : useLegacyNotes ? (
          <LegacySOAPForm control={control} note={note} />
        ) : (
          <SOAPForm note={note} />
        )}
      </div>

      {shouldShowFeedbackWidget &&
        note?.jobType !== "dictation" &&
        note?.isAutoSoap &&
        !note?.isMarkedAsFailed && (
          <div className="relative h-12 mb-6">
            <PromptFeedbackWidget
              jobType={note?.jobType}
              noteUuid={note?.uuid}
              userUuid={note?.user?.uuid}
              feedback={note?.feedback}
              thumbsUp={note?.thumbsUp}
            />
          </div>
        )}

      <div className="py-5">
        {!isScribeEdit && !isTemplate && (
          <div className="flex flex-col space-y-sm">
            {note?.patient && (
              <div id="additionalInfo">
                <Accordion
                  defaultExpanded={false}
                  disableGutters={true}
                  sx={{
                    "&::before": {
                      backgroundColor: "transparent",
                    },
                    boxShadow:
                      "0px 0px 5px 0px var(--shadow-primary)",
                    borderRadius: "100px",
                    backgroundColor: "var(--surface-primary)",
                    marginTop: "10px",
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <FontAwesomeIcon
                        className="dark:text-gray-300"
                        icon={faChevronDown}
                      />
                    }
                  >
                    <div>
                      <h4>
                        <Twemoji emoji="➕" /> Additional Key
                        Information
                      </h4>
                      <h6 className="text-secondary">
                        If you would like to include additional key
                        information regarding this patient, type it in
                        below and it will be added to their notebook.
                      </h6>
                    </div>
                  </AccordionSummary>

                  <AccordionDetails>
                    <KeyPatientInfoFormFields
                      control={control}
                      keyHistory={note?.keyHistory}
                      keyMedications={note?.keyMedications}
                      vaccinationStatus={note?.vaccinationStatus}
                      weight={note?.weight}
                    />
                  </AccordionDetails>
                </Accordion>
              </div>
            )}

            <div id="addAttachments">
              <Accordion
                defaultExpanded={false}
                disableGutters={true}
                sx={{
                  "&::before": {
                    backgroundColor: "transparent",
                  },
                  boxShadow: "0px 0px 5px 0px var(--shadow-primary)",
                  borderRadius: "100px",
                  backgroundColor: "var(--surface-primary)",
                  marginTop: "10px",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <FontAwesomeIcon
                      className="dark:text-gray-300"
                      icon={faChevronDown}
                    />
                  }
                >
                  <div>
                    <h4 data-testid="add-attachments-header">
                      <Twemoji emoji="📎" /> Add Attachments
                    </h4>
                    <h6 className="text-secondary">
                      Accepted file types: pictures, videos, PDFs,
                      documents.
                    </h6>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div id="addAttachments" className="my-2">
                    <AttachmentSelection
                      noteUuid={note?.uuid}
                      onUploadComplete={refetchNote}
                    />
                  </div>
                  {note?.attachments && (
                    <AttachmentsPreview
                      attachments={note?.attachments}
                      isCompactView={true}
                      attachmentPreviewText={
                        "Files attached to this note"
                      }
                      emptyStateText={
                        "No files are currently attached to this note."
                      }
                      onDelete={handleDeleteAttachment}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </div>

            <div id="noteEmailSection">
              <Accordion
                onChange={(_, expanded) =>
                  setExportExpanded(expanded)
                }
                defaultExpanded={false}
                disableGutters={true}
                sx={{
                  "&::before": {
                    backgroundColor: "transparent",
                  },
                  boxShadow: "0px 0px 5px 0px var(--shadow-primary)",
                  borderRadius: "100px",
                  backgroundColor: "var(--surface-primary)",
                  marginTop: "10px",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <FontAwesomeIcon
                      className="dark:text-gray-300"
                      icon={faChevronDown}
                    />
                  }
                >
                  <div>
                    <h4 data-testid="export-note-header">
                      <Twemoji emoji="📤" /> Export Note
                    </h4>
                    <h6 className="text-secondary">
                      Bring this note into your PIMS, or export it in
                      other ways.
                    </h6>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="flex flex-row space-x-4">
                    {window.documentPictureInPicture && (
                      <DSCardWrapper variant="info">
                        <Tooltip
                          arrow
                          placement="right"
                          title="Widget Mode supports all PIMS - Avimark, Cornerstone, ezyVet, Impromed, etc."
                        >
                          <span>
                            <DSBadge size="sm" icon={faInfoCircle}>
                              For Cloud-Based and Server-Based PIMS
                            </DSBadge>
                          </span>
                        </Tooltip>
                        <p className="my-sm">
                          Widget Mode helps you easily drag and drop
                          this note into your PIMS. No install
                          required!
                        </p>
                        <OpenPipButton
                          pipUrl={`/pip/note/${note?.uuid}`}
                          isBadgeEnabled={true}
                        />
                      </DSCardWrapper>
                    )}
                    <DSCardWrapper variant="info">
                      <Tooltip
                        arrow
                        placement="right"
                        title="PIMSPal currently only supports PIMS like ezyVet, Pulse, Vetspire, etc."
                      >
                        <span>
                          <DSBadge size="sm" icon={faInfoCircle}>
                            For Cloud-Based PIMS
                          </DSBadge>
                        </span>
                      </Tooltip>
                      <p className="my-sm">
                        For a more <em>auto-magic</em> experience,
                        download our PIMSPal extension! Once
                        installed, PIMSPal will appear in your PIMS's
                        text fields, bringing your notes right where
                        you need them.
                      </p>
                      <DSLinkButton
                        icon={faCloudDownload}
                        href="https://chromewebstore.google.com/detail/scribenote-pimspal/ibenkaeanlddcgpbfokocbhgbpfhieoc"
                        target="_blank"
                      >
                        Download PIMSPal for Google Chrome
                      </DSLinkButton>
                    </DSCardWrapper>
                  </div>
                  <HeadingWrapper
                    heading={<h4>Other Export Options</h4>}
                  />
                  <ExportNoteInline
                    getValues={getValues}
                    exportExpanded={exportExpanded}
                    note={note}
                    onEmailClick={() => setShowEmailModal(true)}
                  />
                </AccordionDetails>
              </Accordion>
            </div>

            <div
              id="clientSummary"
              data-testid="client-summary-accordion"
            >
              <Accordion
                defaultExpanded={false}
                disableGutters={true}
                sx={{
                  "&::before": {
                    backgroundColor: "transparent",
                  },
                  boxShadow: "0px 0px 5px 0px var(--shadow-primary)",
                  borderRadius: "100px",
                  backgroundColor: "var(--surface-primary)",
                  marginTop: "10px",
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <FontAwesomeIcon
                      className="dark:text-gray-300"
                      icon={faChevronDown}
                    />
                  }
                >
                  <div>
                    <h4 data-testid="client-summary-header">
                      <Twemoji emoji="🏠" />{" "}
                      <span className="ml-1">Client Summary</span>
                    </h4>

                    <h6 className="text-secondary">
                      Automatically generate a summary for your client
                      based on your note!
                    </h6>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  {!shouldShowFreePlanState ? (
                    <ClientSummary
                      note={note}
                      refetchNote={refetchNote}
                    />
                  ) : (
                    <ClientSummaryFreePlanState
                      noteUuid={note?.uuid}
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
